import { MasterDataAction, set as setMasterData } from '@spike/masterdata-action';
// Simple error handling without external notifications
import ApiClient, { isCancelled } from 'api/ApiClient';
import {
    removeFromStorage,
    saveInStorage,
    setOrgAccessLevelInStorage,
    setTimeZoneInStorage,
    changeAvatarInStorage
} from 'auth';
import { AxiosResponse } from 'axios';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { LoginState } from 'reducers/login/LoginState';
import { ThunkAction } from 'redux-thunk';
import { LoginPath } from 'routes/Paths';
import store from 'store';
import {
    LoginAction,
    LOGIN_END_ACTION_TYPE,
    LOGIN_FAIL_ACTION_TYPE,
    LOGIN_RESET_ACTION_TYPE,
    LOGIN_START_ACTION_TYPE,
    LOGIN_SUCCESS_ACTION_TYPE,
    LOGOUT_ACTION_TYPE,
    LOGIN_SET_ORG_ACCESS_LEVEL_ACTION_TYPE,
    LOGIN_CHANGE_IMAGE_ACTION_TYPE
} from './LoginActionsTypes';
import { convertOrganizationsLogin, convertOrganizationsUser } from './OrganizationsLoginConverter';
import { OrganizationsLoginResponseDto, OrganizationsLoginRequestDto } from './OrganizationsLoginDtos';

export const resetThunk = (): ThunkAction<void, LoginState, null, LoginAction> => dispatch => {
    dispatch(reset());
};

export const reset = (): LoginAction => ({
    type: LOGIN_RESET_ACTION_TYPE
});

export const loginThunk =
    (
        email: string,
        password: string
    ): ThunkAction<void, LoginState, null, LoginAction | MasterDataAction | CallHistoryMethodAction> =>
    async dispatch => {
        dispatch(start());

        const request: OrganizationsLoginRequestDto = {
            email,
            password
        };

        try {
            // Add cache-busting headers to prevent browser caching of login requests
            const response: AxiosResponse<OrganizationsLoginResponseDto> = await ApiClient.get('/login_orgadmin', {
                params: request,
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });

            const loginResponse = convertOrganizationsLogin(response.data);

            // Validate org_access_level_id - only org admin (1) and org manager (2) allowed
            const orgAccessLevel = loginResponse.user.org_access_level_id;
            if (orgAccessLevel !== 1 && orgAccessLevel !== 2) {
                throw new Error('Access denied. Only Organization Admins and Managers can access this system.');
            }

            // Create Auth object using the converter
            const authData = convertOrganizationsUser(loginResponse.user);

            // Set timezone from response
            if (loginResponse.time_zone) {
                authData.timeZone = loginResponse.time_zone;
            }

            // Save authentication data (save in session storage, not local)
            saveInStorage(authData, false);
            setOrgAccessLevelInStorage(loginResponse.user.org_access_level_id);

            if (loginResponse.time_zone) {
                // For organizations, we don't need marketplace-specific timezone storage
                // We'll store it globally for the organization user
                setTimeZoneInStorage(loginResponse.time_zone, 0); // 0 = organization level
            }

            // Set master data if available
            if (loginResponse.master_data) {
                dispatch(setMasterData(loginResponse.master_data));
            }

            // Success
            dispatch(success({ auth: authData }));

            // Redirect to dashboard
            dispatch(push('/dashboard'));
        } catch (error: unknown) {
            if (!isCancelled(error)) {
                // Type guard to safely access error properties
                const axiosError = error as {
                    response?: { data?: { message?: string; error?: string } };
                    message?: string;
                };
                const errorMessage =
                    axiosError?.response?.data?.message ||
                    axiosError?.response?.data?.error ||
                    axiosError?.message ||
                    'Invalid credentials. Please check your email and password.';

                dispatch(fail(errorMessage));
            }
        } finally {
            dispatch(end());
        }
    };

export const logoutThunk =
    (): ThunkAction<void, LoginState, null, LoginAction | CallHistoryMethodAction> => dispatch => {
        // Clear storage
        removeFromStorage();

        // Reset store
        store.dispatch({ type: 'RESET_STORE' });

        // Dispatch logout action
        dispatch(logout());

        // Redirect to login
        dispatch(push(LoginPath));
    };

export const changeAvatar = (image: string, staffId: number, dispatch: any) => {
    if (store.getState().login.auth.user?.staffId === staffId) {
        dispatch(changeImage(image));
        changeAvatarInStorage(image, staffId);
    }
};

// Action creators
const start = (): LoginAction => ({
    type: LOGIN_START_ACTION_TYPE
});

const success = (payload: { auth: any }): LoginAction => ({
    type: LOGIN_SUCCESS_ACTION_TYPE,
    payload
});

const fail = (error: string): LoginAction => {
    const errorMessage = error && error.trim() ? error : 'An error occurred during login. Please try again.';
    return {
        type: LOGIN_FAIL_ACTION_TYPE,
        payload: { error: errorMessage }
    };
};

const end = (): LoginAction => ({
    type: LOGIN_END_ACTION_TYPE
});

const logout = (): LoginAction => ({
    type: LOGOUT_ACTION_TYPE
});

export const setOrgAccessLevel = (level: number): LoginAction => ({
    type: LOGIN_SET_ORG_ACCESS_LEVEL_ACTION_TYPE,
    payload: { org_access_level_id: level }
});

const changeImage = (image: string): LoginAction => {
    return {
        type: LOGIN_CHANGE_IMAGE_ACTION_TYPE,
        payload: {
            image
        }
    };
};
